/**
 * Invite Stats Component
 *
 * Dashboard statistics for invites.
 *
 * @fileoverview Invite statistics component
 */

import React from 'react';
import {
  SimpleGrid,
  Box,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  useColorModeValue,
  Skeleton,
} from '@chakra-ui/react';
import {
  MdEmail,
  MdCheckCircle,
  MdSchedule,
  MdError,
} from 'react-icons/md';
import type { InviteStats as InviteStatsType } from '../../types/admin';

interface InviteStatsProps {
  stats: InviteStatsType | null;
  loading: boolean;
}

interface StatCardProps {
  label: string;
  value: number;
  helpText?: string;
  icon: React.ComponentType;
  colorScheme: string;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  helpText,
  icon,
  colorScheme,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconBg = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
  const iconColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.200`);

  return (
    <Box
      bg={bgColor}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      p={6}
    >
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Stat>
          <StatLabel color="gray.500" fontSize="sm">
            {label}
          </StatLabel>
          <StatNumber fontSize="2xl" fontWeight="bold">
            {value}
          </StatNumber>
          {helpText && (
            <StatHelpText color="gray.400" fontSize="xs">
              {helpText}
            </StatHelpText>
          )}
        </Stat>
        <Box
          p={3}
          borderRadius="lg"
          bg={iconBg}
        >
          <Icon as={icon} boxSize={6} color={iconColor} />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Invite Stats Component
 *
 * Displays invite statistics in a grid of cards.
 */
export const InviteStats: React.FC<InviteStatsProps> = ({ stats, loading }) => {
  // Hoisted above the `loading` early return below: these used to be called
  // inline inside the skeleton's .map(), so they only ran while loading was
  // true — React warns ("has detected a change in the order of Hooks")
  // once `loading` flips to false on this same mounted instance and the
  // hook count for this component drops from 8 to 0 between renders.
  const skeletonBg = useColorModeValue('white', 'gray.800');
  const skeletonBorderColor = useColorModeValue('gray.200', 'gray.700');

  if (loading) {
    return (
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
        {[1, 2, 3, 4].map((i) => (
          <Box
            key={i}
            bg={skeletonBg}
            borderRadius="lg"
            border="1px"
            borderColor={skeletonBorderColor}
            p={6}
          >
            <Skeleton h="20px" w="60%" mb={2} />
            <Skeleton h="32px" w="40%" mb={2} />
            <Skeleton h="16px" w="80%" />
          </Box>
        ))}
      </SimpleGrid>
    );
  }

  if (!stats) return null;

  return (
    <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
      <StatCard
        label="Total Invites"
        value={stats.total}
        helpText="All invites sent"
        icon={MdEmail}
        colorScheme="blue"
      />
      <StatCard
        label="Active Invites"
        value={stats.active}
        helpText="Pending registration"
        icon={MdSchedule}
        colorScheme="cyan"
      />
      <StatCard
        label="Used Invites"
        value={stats.used}
        helpText="Successfully registered"
        icon={MdCheckCircle}
        colorScheme="green"
      />
      <StatCard
        label="Expired Invites"
        value={stats.expired}
        helpText="Past expiration date"
        icon={MdError}
        colorScheme="orange"
      />
    </SimpleGrid>
  );
};

export default InviteStats;
